const performancePoints = [
  'PageLoaded', // Triggered as soon as first script execution in webpage is run

  'AppInitiated', // Triggered when root Vue component App.vue is mounted

  // Trip overview - itinerary
  'TripOverviewPageRendered', // Triggered when loading TripOverview page is first rendered
  'FirstItineraryRequested', // Triggered before sending request to smart search
  'FirstItineraryReceived', // Triggered when response for first day is loaded from smart search
  'FirstItineraryRendered', // Triggered when itinerary for first day is rendered

  // HotelSelection
  'HotelSelectionViewInitiated',
  'HotelSelectionListRendered',

  // ActivitySelection
  'ActivitySelectionViewInitiated',
  'ActivitySelectionListRendered',

  // Common Selection
  'DestinationAvailabilityRequested',
  'DestinationAvailabilityReceived',
] as const;

type PerformancePoint = (typeof performancePoints)[number];

export function performanceMark(point: PerformancePoint): PerformanceMark {
  return performance.mark(point);
}

export function performanceClearMarks(point?: PerformancePoint): void {
  performance.clearMarks(point);
}

function performanceMeasure(
  firstPoint: PerformancePoint,
  secondPoint?: PerformancePoint
): PerformanceMeasure {
  if (!secondPoint) {
    return performance.measure(`initial-${firstPoint}`, {
      duration: performance.getEntriesByName(firstPoint)[0]?.startTime,
      end: firstPoint,
    });
  }

  return performance.measure(`${firstPoint}-${secondPoint}`, {
    start: firstPoint,
    end: secondPoint,
  });
}

interface InitializationPerformanceMeasures {
  timeToPageLoadedMs: number;
  timeToAppInitiatedMs: number;
}
export function measureInitializationPerformance(): Promise<InitializationPerformanceMeasures> {
  return new Promise((resolve) => {
    const observerCallback: PerformanceObserverCallback = (list, observer) => {
      list.getEntries().forEach((entry) => {
        if ((entry.name as PerformancePoint) === 'AppInitiated') {
          const timeToPageLoadedMs = performanceMeasure('PageLoaded').duration;
          const timeToAppInitiatedMs = performanceMeasure(
            'PageLoaded',
            'AppInitiated'
          ).duration;

          observer.disconnect();

          resolve({
            timeToPageLoadedMs,
            timeToAppInitiatedMs,
          });
        }
      });
    };

    const observer = new PerformanceObserver(observerCallback);

    observer.observe({ entryTypes: ['mark'] });
  });
}

interface FirstItineraryPerformanceMeasures {
  timeToTripOverviewPageRendered: number;
  timeToFirstItineraryAvailabilityRequestMs: number;
  timeToFirstItineraryAvailabilityReceivedMs: number;
  timeToFirstItineraryRenderedMs: number;
  totalTimeRequiredToRenderFirstItinerary: number;
}
export function measureFirstItineraryRenderPerformance(): Promise<FirstItineraryPerformanceMeasures> {
  return new Promise((resolve) => {
    const observerCallback: PerformanceObserverCallback = (list, observer) => {
      list.getEntries().forEach((entry) => {
        if ((entry.name as PerformancePoint) === 'FirstItineraryRendered') {
          const timeToTripOverviewPageRendered = performanceMeasure(
            'AppInitiated',
            'TripOverviewPageRendered'
          ).duration;
          const timeToFirstItineraryAvailabilityRequestMs = performanceMeasure(
            'AppInitiated',
            'FirstItineraryRequested'
          ).duration;
          const timeToFirstItineraryAvailabilityReceivedMs = performanceMeasure(
            'FirstItineraryRequested',
            'FirstItineraryReceived'
          ).duration;
          const timeToFirstItineraryRenderedMs = performanceMeasure(
            'FirstItineraryReceived',
            'FirstItineraryRendered'
          ).duration;
          const totalTimeRequiredToRenderFirstItinerary = performanceMeasure(
            'AppInitiated',
            'FirstItineraryRendered'
          ).duration;

          observer.disconnect();

          resolve({
            timeToTripOverviewPageRendered,
            timeToFirstItineraryAvailabilityRequestMs,
            timeToFirstItineraryAvailabilityReceivedMs,
            timeToFirstItineraryRenderedMs,
            totalTimeRequiredToRenderFirstItinerary,
          });
        }
      });
    };

    const observer = new PerformanceObserver(observerCallback);

    observer.observe({ entryTypes: ['mark'] });
  });
}

interface HotelSelectionPerformanceMeasures {
  timeToHotelSelectionViewAvailabilityRequestMs: number;
  timeToHotelSelectionViewAvailabilityReceivedMs: number;
  timeToHotelSelectionListRenderedMs: number;
  totalTimeRequiredToRenderHotelSelectionList: number;
}
export function measureHotelSelectionListRenderPerformance(): Promise<HotelSelectionPerformanceMeasures> {
  return new Promise((resolve) => {
    const observerCallback: PerformanceObserverCallback = (list) => {
      list.getEntries().forEach((entry) => {
        if ((entry.name as PerformancePoint) === 'HotelSelectionListRendered') {
          const timeToHotelSelectionViewAvailabilityRequestMs =
            performanceMeasure(
              'HotelSelectionViewInitiated',
              'DestinationAvailabilityRequested'
            ).duration;
          const timeToHotelSelectionViewAvailabilityReceivedMs =
            performanceMeasure(
              'DestinationAvailabilityRequested',
              'DestinationAvailabilityReceived'
            ).duration;
          const timeToHotelSelectionListRenderedMs = performanceMeasure(
            'DestinationAvailabilityReceived',
            'HotelSelectionListRendered'
          ).duration;
          const totalTimeRequiredToRenderHotelSelectionList =
            performanceMeasure(
              'HotelSelectionViewInitiated',
              'HotelSelectionListRendered'
            ).duration;

          performanceClearMarks('DestinationAvailabilityRequested');
          performanceClearMarks('DestinationAvailabilityReceived');

          observer.disconnect();

          resolve({
            timeToHotelSelectionViewAvailabilityRequestMs,
            timeToHotelSelectionViewAvailabilityReceivedMs,
            timeToHotelSelectionListRenderedMs,
            totalTimeRequiredToRenderHotelSelectionList,
          });
        }
      });
    };

    const observer = new PerformanceObserver(observerCallback);

    observer.observe({ entryTypes: ['mark'] });
  });
}

interface ActivitySelectionPerformanceMeasures {
  timeToActivitySelectionViewAvailabilityRequestMs: number;
  timeToActivitySelectionViewAvailabilityReceivedMs: number;
  timeToActivitySelectionListRenderedMs: number;
  totalTimeRequiredToRenderActivitySelectionList: number;
}
export function measureActivitySelectionListRenderPerformance(): Promise<ActivitySelectionPerformanceMeasures> {
  return new Promise((resolve) => {
    const observerCallback: PerformanceObserverCallback = (list) => {
      list.getEntries().forEach((entry) => {
        if (
          (entry.name as PerformancePoint) === 'ActivitySelectionListRendered'
        ) {
          const timeToActivitySelectionViewAvailabilityRequestMs =
            performanceMeasure(
              'ActivitySelectionViewInitiated',
              'DestinationAvailabilityRequested'
            ).duration;
          const timeToActivitySelectionViewAvailabilityReceivedMs =
            performanceMeasure(
              'DestinationAvailabilityRequested',
              'DestinationAvailabilityReceived'
            ).duration;
          const timeToActivitySelectionListRenderedMs = performanceMeasure(
            'DestinationAvailabilityReceived',
            'ActivitySelectionListRendered'
          ).duration;
          const totalTimeRequiredToRenderActivitySelectionList =
            performanceMeasure(
              'ActivitySelectionViewInitiated',
              'ActivitySelectionListRendered'
            ).duration;

          performanceClearMarks('DestinationAvailabilityRequested');
          performanceClearMarks('DestinationAvailabilityReceived');

          observer.disconnect();

          resolve({
            timeToActivitySelectionViewAvailabilityRequestMs,
            timeToActivitySelectionViewAvailabilityReceivedMs,
            timeToActivitySelectionListRenderedMs,
            totalTimeRequiredToRenderActivitySelectionList,
          });
        }
      });
    };

    const observer = new PerformanceObserver(observerCallback);

    observer.observe({ entryTypes: ['mark'] });
  });
}
